import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useRapat, useCreateRapat, useUpdateRapat, useDeleteRapat } from '@/hooks/useRapat'
import { useAuth } from '@/hooks/useAuth'
import { useAuthStore } from '@/stores/authStore'
import Button from '@/components/ui/Button'
import Card from '@/components/ui/Card'
import pb from '@/lib/pocketbase'

interface FormData {
  judul: string
  tanggal: string
  waktu_mulai: string
  waktu_selesai: string
  tempat: string
  agenda: string
  hasil_rapat: string
  notulis_id: string
  status: 'rencana' | 'berlangsung' | 'selesai' | 'dibatalkan'
  published: boolean
}

export default function RapatFormPage() {
  const { kegiatanId, rapatId } = useParams<{ kegiatanId: string; rapatId: string }>()
  const navigate = useNavigate()
  const { isSuperAdmin, isStadion, isStadionFor } = useAuth()
  const { user } = useAuthStore()
  const canEdit = isSuperAdmin || (isStadion && isStadionFor(kegiatanId!))

  const { data: rapat, isLoading } = useRapat(rapatId)
  const createRapat = useCreateRapat()
  const updateRapat = useUpdateRapat()
  const deleteRapat = useDeleteRapat()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      status: 'rencana',
      published: false,
      notulis_id: '',
    },
  })

  const watchStatus = watch('status')

  useEffect(() => {
    if (rapat) {
      setValue('judul', rapat.judul)
      setValue('tanggal', rapat.tanggal)
      setValue('waktu_mulai', rapat.waktu_mulai || '')
      setValue('waktu_selesai', rapat.waktu_selesai || '')
      setValue('tempat', rapat.tempat || '')
      setValue('agenda', rapat.agenda || '')
      setValue('hasil_rapat', rapat.hasil_rapat || '')
      setValue('notulis_id', rapat.notulis_id || '')
      setValue('status', rapat.status)
      setValue('published', rapat.published)
    } else if (!isLoading && !rapatId) {
      // Default notulis to current user
      if (user?.id) {
        setValue('notulis_id', user.id)
      }
    }
  }, [rapat, isLoading, rapatId, user, setValue])

  const onSubmit = async (data: FormData) => {
    const payload: Record<string, unknown> = {
      ...data,
      kegiatan: kegiatanId,
      created_by: user?.id,
    }

    if (rapatId) {
      await updateRapat.mutateAsync({ id: rapatId, data: payload })
    } else {
      await createRapat.mutateAsync(payload as Parameters<typeof createRapat.mutateAsync>[0])
    }
    navigate(`/kegiatan/${kegiatanId}`)
  }

  const onDelete = async () => {
    if (!rapatId) return
    if (!confirm('Hapus rapat ini?')) return
    await deleteRapat.mutateAsync({ id: rapatId, kegiatanId: kegiatanId! })
    navigate(`/kegiatan/${kegiatanId}`)
  }

  if (!canEdit) {
    return (
      <div className="p-4">
        <p className="text-sm text-red-500">Anda tidak memiliki akses untuk mengedit rapat.</p>
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} className="mt-3">
          Kembali
        </Button>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <div className="bg-white rounded-card h-32 animate-pulse" />
        <div className="bg-white rounded-card h-48 animate-pulse" />
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4 max-w-lg mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-lg font-bold">
          {rapatId ? 'Edit Rapat' : 'Rapat Baru'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Card>
          <h3 className="text-sm font-semibold mb-3 text-primary">Detail Rapat</h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-text-secondary mb-1 block">Judul Rapat *</label>
              <input
                {...register('judul', { required: 'Judul wajib diisi' })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="cth: Rapat Persiapan Teknis"
              />
              {errors.judul && <p className="text-xs text-red-500 mt-1">{errors.judul.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-text-secondary mb-1 block">Tanggal *</label>
                <input
                  type="date"
                  {...register('tanggal', { required: 'Tanggal wajib diisi' })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                {errors.tanggal && <p className="text-xs text-red-500 mt-1">{errors.tanggal.message}</p>}
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary mb-1 block">Status</label>
                <select
                  {...register('status')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="rencana">Rencana</option>
                  <option value="berlangsung">Berlangsung</option>
                  <option value="selesai">Selesai</option>
                  <option value="dibatalkan">Dibatalkan</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-text-secondary mb-1 block">Waktu Mulai</label>
                <input
                  type="time"
                  {...register('waktu_mulai')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-text-secondary mb-1 block">Waktu Selesai</label>
                <input
                  type="time"
                  {...register('waktu_selesai')}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-text-secondary mb-1 block">Tempat</label>
              <input
                {...register('tempat')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="cth: Ruang Sidang Utama"
              />
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold mb-3 text-primary">Agenda & Hasil</h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-text-secondary mb-1 block">Agenda / Pokok Bahasan</label>
              <textarea
                {...register('agenda')}
                rows={4}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                placeholder="Ketik agenda rapat..."
              />
            </div>

            <div>
              <label className="text-xs font-medium text-text-secondary mb-1 block">Hasil / Kesimpulan</label>
              <textarea
                {...register('hasil_rapat')}
                rows={3}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                placeholder="Ketik hasil rapat..."
              />
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold mb-3 text-primary">Notulis & Akses</h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-text-secondary mb-1 block">Notulis</label>
              <input
                type="text"
                {...register('notulis_id')}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Ketik nama notulis atau kosongkan"
              />
              <p className="text-xs text-text-secondary mt-1">Kosongkan jika notulis belum ditentukan.</p>
            </div>

            {watchStatus === 'selesai' && (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('published')}
                  className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm">Publikasikan hasil rapat untuk Anggota</span>
              </label>
            )}
          </div>
        </Card>

        <div className="flex gap-3">
          {rapatId && (
            <Button
              type="button"
              variant="outline"
              onClick={onDelete}
              disabled={isSubmitting || deleteRapat.isPending}
              className="text-red-500 hover:bg-red-50"
            >
              Hapus
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting || createRapat.isPending || updateRapat.isPending}
            className="flex-1"
          >
            {rapatId ? 'Simpan Perubahan' : 'Simpan Rapat'}
          </Button>
        </div>
      </form>
    </div>
  )
}
